<template>
  <v-col cols="12">
    <v-toolbar class="mb-2 mt-5 pl-2" color="grey-darken-2" dense>
      <div style="color: white">
        <strong>{{ $t("institution_compliance.title") }}</strong>
      </div>
    </v-toolbar>
    <v-progress-linear v-if="loading" indeterminate color="red-darken-1" />
    <v-alert v-if="error" type="error" variant="tonal" class="mb-3">
      {{ error }}
    </v-alert>
    <template v-if="state">
      <v-alert
        v-if="!state.globalEnabled"
        type="warning"
        variant="tonal"
        class="mb-3"
      >
        {{ $t("institution_compliance.global_off") }}
      </v-alert>
      <div class="text-body-2 text-medium-emphasis mb-2">
        {{ $t("institution_compliance.hint") }}
      </div>
      <v-switch
        :model-value="state.enabled"
        :label="$t('institution_compliance.module')"
        :disabled="saving"
        color="red-darken-1"
        hide-details
        @update:model-value="ask('compliance', $event)"
      />
      <v-switch
        :model-value="state.praEnabled"
        :label="$t('institution_compliance.pra')"
        :disabled="saving || (!state.enabled && !state.praEnabled)"
        :hint="
          state.enabled ? '' : $t('institution_compliance.pra_needs_module')
        "
        :persistent-hint="!state.enabled"
        color="red-darken-1"
        @update:model-value="ask('pra', $event)"
      />
      <v-switch
        :model-value="state.workshopEnabled"
        :label="$t('institution_compliance.workshop')"
        :disabled="saving || (!state.enabled && !state.workshopEnabled)"
        :hint="
          state.enabled
            ? ''
            : $t('institution_compliance.workshop_needs_module')
        "
        :persistent-hint="!state.enabled"
        color="red-darken-1"
        @update:model-value="ask('workshop', $event)"
      />
      <!-- Stage 6: waste needs the workshop on; turning it off is always allowed. -->
      <v-switch
        :model-value="state.wasteEnabled"
        :label="$t('institution_compliance.waste.label')"
        :disabled="saving || (!state.workshopEnabled && !state.wasteEnabled)"
        :hint="
          state.workshopEnabled
            ? ''
            : $t('institution_compliance.waste.needs_workshop')
        "
        :persistent-hint="!state.workshopEnabled"
        color="red-darken-1"
        @update:model-value="ask('waste', $event)"
      />
    </template>

    <v-dialog v-model="confirm.open" max-width="480">
      <v-card>
        <v-card-text v-if="setupResult" class="pt-5">
          <div class="text-body-1">
            {{ $t("institution_compliance.waste.done") }}
          </div>
          <div class="text-body-2 mt-3">
            {{
              $t("institution_compliance.waste.done_codes", {
                total: setupResult.codesTotal,
                inserted: setupResult.codesInserted,
                version: setupResult.listVersion,
              })
            }}
          </div>
          <div class="text-body-2 mt-1">
            {{
              $t(
                setupResult.areasCreated
                  ? "institution_compliance.waste.done_area"
                  : "institution_compliance.waste.done_area_existing",
                { count: setupResult.areasCreated },
              )
            }}
          </div>
        </v-card-text>
        <v-card-text v-else class="pt-5">
          <div class="text-body-1">{{ confirmText }}</div>
          <div
            v-if="confirm.stage === 'pra' && confirm.enabled"
            class="text-body-2 text-medium-emphasis mt-3"
          >
            {{ $t("institution_compliance.pra_backfill_note") }}
          </div>
          <div
            v-if="confirm.stage === 'workshop' && confirm.enabled"
            class="text-body-2 text-medium-emphasis mt-3"
          >
            {{ $t("institution_compliance.workshop_backfill_note") }}
          </div>
          <div
            v-if="confirm.stage === 'waste' && confirm.enabled"
            class="text-body-2 text-medium-emphasis mt-3"
          >
            {{ $t("institution_compliance.waste.setup_note") }}
          </div>
        </v-card-text>
        <v-card-actions v-if="setupResult">
          <v-spacer />
          <v-btn color="green-darken-1" variant="outlined" @click="closeSetup">
            {{ $t("institution_compliance.waste.close") }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-spacer />
          <v-btn
            color="red-darken-1"
            variant="outlined"
            :disabled="saving"
            @click="confirm.open = false"
          >
            {{ $t("institution_compliance.cancel") }}
          </v-btn>
          <v-btn
            color="green-darken-1"
            variant="outlined"
            :loading="saving"
            @click="save"
          >
            {{ $t("institution_compliance.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.value" location="top" color="success">
      {{ snackbar.message }}
    </v-snackbar>
  </v-col>
</template>

<script>
import {
  getInstitutionSwitch,
  newCommandId,
  setInstitutionSwitch,
} from "@/api/compliance";

// Per-institution Dismantling & Compliance, PRA, workshop and waste switches (SuperAdmin). Each change is
// confirmed, then saved on its own, apart from the institution form's Save button.
export default {
  name: "ComplianceSwitches",
  props: {
    institutionId: { type: String, required: true },
    institutionTitle: { type: String, default: "" },
  },
  data() {
    return {
      state: null,
      loading: false,
      saving: false,
      error: "",
      confirm: { open: false, stage: "", enabled: false, commandId: "" },
      // Waste ON: the seed counts, shown in the dialog until it is closed.
      setupResult: null,
      snackbar: { value: false, message: "" },
    };
  },
  computed: {
    confirmText() {
      const names = {
        compliance: "institution_compliance.module",
        pra: "institution_compliance.pra",
        workshop: "institution_compliance.workshop",
        waste: "institution_compliance.waste.label",
      };
      const name = this.$t(names[this.confirm.stage] || names.compliance);
      const key = this.confirm.enabled ? "confirm_on" : "confirm_off";
      return this.$t(`institution_compliance.${key}`, {
        name,
        institution: this.institutionTitle,
      });
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.state = await getInstitutionSwitch(this.institutionId);
      } catch (err) {
        this.error = this.errorText(err);
      } finally {
        this.loading = false;
      }
    },
    ask(stage, enabled) {
      this.setupResult = null;
      this.confirm = {
        open: true,
        stage,
        enabled: enabled === true,
        commandId: newCommandId(),
      };
    },
    async save() {
      this.saving = true;
      this.error = "";
      try {
        const result = await setInstitutionSwitch(this.institutionId, {
          stage: this.confirm.stage,
          enabled: this.confirm.enabled,
          revision: this.state.revision,
          commandId: this.confirm.commandId,
        });
        this.state = result;
        if (result.setup) {
          // The dialog stays open with the counts.
          this.setupResult = result.setup;
          return;
        }
        this.confirm.open = false;
        this.snackbar = {
          value: true,
          message: result.backfill
            ? this.$t(
                this.confirm.stage === "workshop"
                  ? "institution_compliance.workshop_backfill_done"
                  : "institution_compliance.backfill_done",
                { created: result.backfill.created },
              )
            : this.$t("institution_compliance.saved"),
        };
      } catch (err) {
        this.confirm.open = false;
        const code =
          err && err.response && err.response.data && err.response.data.code;
        if (code === "revision_conflict") {
          await this.load();
          this.error = this.$t("institution_compliance.conflict");
        } else {
          this.error = this.errorText(err);
        }
      } finally {
        this.saving = false;
      }
    },
    closeSetup() {
      this.confirm.open = false;
      this.setupResult = null;
    },
    errorText(err) {
      return (
        (err &&
          err.response &&
          err.response.data &&
          err.response.data.errorMsg) ||
        (err && err.message) ||
        "Error"
      );
    },
  },
};
</script>
